import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { conversions, storeUTMParams, trackPageView } from '../utils/analytics';

const PrisonFamilies = () => {
  const navigate = useNavigate();

  useEffect(() => {
    storeUTMParams();
    trackPageView('/prison-families', 'Prison Families - Always Here');
    conversions.prisonLandingViewed();
  }, []);

  const handleGetStarted = () => {
    conversions.prisonCTAClicked();
    navigate('/register?plan=prison');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-900 to-purple-700">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        <div className="text-center">
          <div className="inline-block bg-green-400 text-purple-900 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            💚 AFFORDABLE ALTERNATIVE TO PRISON PHONE CALLS
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Stop Paying $500/Month<br />for Prison Phone Calls
          </h1>
          
          <p className="text-2xl text-purple-100 mb-4 max-w-3xl mx-auto">
            Your child can hear their parent's voice unlimited times for just $49/month.
            No per-minute fees. No time limits. No more expensive prison calls.
          </p>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 max-w-2xl mx-auto mb-8">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-red-300 text-sm mb-1">Prison Calls</div>
                <div className="text-4xl font-bold text-white">$500</div>
                <div className="text-purple-200 text-sm">per month</div>
              </div>
              <div>
                <div className="text-green-300 text-sm mb-1">With Always Here</div>
                <div className="text-4xl font-bold text-white">$49</div>
                <div className="text-purple-200 text-sm">per month</div>
              </div>
            </div>
            <div className="mt-4 text-green-300 font-bold text-xl">
              Save $451/month = $5,412/year
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <button
              onClick={handleGetStarted}
              className="bg-green-400 text-purple-900 px-8 py-4 rounded-lg text-xl font-bold hover:bg-green-300 transition shadow-lg"
            >
              Get Started Free →
            </button>
            <button
              onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg text-xl font-semibold hover:bg-white hover:text-purple-900 transition"
            >
              See How It Works
            </button>
          </div>
          
          <p className="text-purple-200 text-sm">
            ✅ First month free • ✅ No hidden fees • ✅ Cancel anytime
          </p>
        </div>
      </div>

      {/* Problem Section */}
      <div className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Prison Phone Companies Are Robbing Families
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-red-50 border-2 border-red-200 p-6 rounded-lg">
              <div className="text-4xl mb-3">💸</div>
              <h3 className="text-xl font-bold text-red-900 mb-2">Exploitative Pricing</h3>
              <p className="text-gray-700">
                $1-3 per minute for calls. A 15-minute call costs $45. 
                Families spend $300-500/month just to talk.
              </p>
            </div>
            
            <div className="bg-red-50 border-2 border-red-200 p-6 rounded-lg">
              <div className="text-4xl mb-3">⏱️</div>
              <h3 className="text-xl font-bold text-red-900 mb-2">Time Limits</h3>
              <p className="text-gray-700">
                15-minute maximum. Call drops mid-conversation. 
                Kids don't get to finish talking to their parent.
              </p>
            </div>
            
            <div className="bg-red-50 border-2 border-red-200 p-6 rounded-lg">
              <div className="text-4xl mb-3">📅</div>
              <h3 className="text-xl font-bold text-red-900 mb-2">Scheduling Nightmare</h3>
              <p className="text-gray-700">
                Must be available at exact time. Kid at school? Miss the call. 
                No flexibility, no recordings.
              </p>
            </div>
          </div>
          
          <div className="bg-green-50 border-2 border-green-500 p-8 rounded-xl">
            <h3 className="text-2xl font-bold text-green-900 mb-4 text-center">
              There's a Better Way
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl mb-2">✅</div>
                <p className="font-semibold text-gray-900">$49/month unlimited</p>
                <p className="text-gray-600 text-sm">No per-minute fees</p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">✅</div>
                <p className="font-semibold text-gray-900">Listen anytime</p>
                <p className="text-gray-600 text-sm">No time limits</p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">✅</div>
                <p className="font-semibold text-gray-900">24/7 access</p>
                <p className="text-gray-600 text-sm">No scheduling needed</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div id="how-it-works" className="bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            How It Works
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🎙️</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">1. Parent Records</h3>
              <p className="text-gray-600 text-center">
                During a prison visit or via approved recording system, parent records 
                bedtime stories, messages, and life advice. Takes 2-3 hours.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">☁️</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">2. We Store & Deliver</h3>
              <p className="text-gray-600 text-center">
                Messages are securely stored and delivered to your child via WhatsApp 
                or phone. Daily bedtime stories, birthday messages, and more.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">💙</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">3. Kids Listen Anytime</h3>
              <p className="text-gray-600 text-center">
                Your child can hear their parent's voice unlimited times. 
                No per-minute charges. No time limits. Just connection.
              </p>
            </div>
          </div>
          
          <div className="bg-purple-100 border-l-4 border-purple-600 p-6 rounded-r-lg">
            <h3 className="text-xl font-bold text-purple-900 mb-2">
              📍 Recording Options
            </h3>
            <ul className="space-y-2 text-gray-700">
              <li>• <strong>Prison visit:</strong> Bring tablet/phone, record during visitation</li>
              <li>• <strong>Prison library:</strong> Many facilities have recording equipment</li>
              <li>• <strong>Phone recording:</strong> Call our system, record via phone (check facility rules)</li>
              <li>• <strong>Mail-in:</strong> Send written messages, we'll convert to audio in parent's voice</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Real Stories from Real Families
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg shadow-lg">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 text-xl">★★★★★</div>
              </div>
              <p className="text-gray-700 mb-4 italic">
                "Prison phone calls were costing us hundreds per month. Being able to record 
                messages during visitation and have them delivered automatically saved us money 
                and gave our kids consistent access to their parent's voice."
              </p>
              <p className="text-gray-900 font-semibold">— Parent (Beta Tester)</p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg shadow-lg">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 text-xl">★★★★★</div>
              </div>
              <p className="text-gray-700 mb-4 italic">
                "The ability to record bedtime stories and messages during prison visits 
                meant my child could hear their parent's voice anytime, not just during 
                expensive 15-minute phone calls."
              </p>
              <p className="text-gray-900 font-semibold">— Family Member (Beta Tester)</p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg shadow-lg">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 text-xl">★★★★★</div>
              </div>
              <p className="text-gray-700 mb-4 italic">
                "Being able to record messages for future milestones during visitation 
                meant I could still be present for my child's important moments, even 
                while incarcerated."
              </p>
              <p className="text-gray-900 font-semibold">— Parent (Beta Tester)</p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg shadow-lg">
              <div className="flex items-center mb-4">
                <div className="text-yellow-400 text-xl">★★★★★</div>
              </div>
              <p className="text-gray-700 mb-4 italic">
                "This platform offers a more affordable way for families to stay connected 
                during incarceration. The ability to record and replay messages helps 
                maintain family bonds without the high cost of prison phone systems."
              </p>
              <p className="text-gray-900 font-semibold">— Social Services Professional</p>
            </div>
          </div>
        </div>
      </div>

      {/* Cost Comparison */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            The Real Cost Comparison
          </h2>
          
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-gray-900 font-bold"></th>
                  <th className="px-6 py-4 text-center text-red-600 font-bold">Prison Calls</th>
                  <th className="px-6 py-4 text-center text-green-600 font-bold">Always Here</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 font-semibold text-gray-900">Monthly Cost</td>
                  <td className="px-6 py-4 text-center text-red-600 font-bold">$300-500</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">$49</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Per-Minute Fees</td>
                  <td className="px-6 py-4 text-center text-red-600">$1-3/min</td>
                  <td className="px-6 py-4 text-center text-green-600">$0</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-semibold text-gray-900">Time Limits</td>
                  <td className="px-6 py-4 text-center text-red-600">15 minutes</td>
                  <td className="px-6 py-4 text-center text-green-600">Unlimited</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Scheduling Required</td>
                  <td className="px-6 py-4 text-center text-red-600">Yes</td>
                  <td className="px-6 py-4 text-center text-green-600">No - 24/7 access</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-semibold text-gray-900">Can Replay Messages</td>
                  <td className="px-6 py-4 text-center text-red-600">No</td>
                  <td className="px-6 py-4 text-center text-green-600">Yes - unlimited</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Annual Cost</td>
                  <td className="px-6 py-4 text-center text-red-600 font-bold text-xl">$3,600-6,000</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold text-xl">$588</td>
                </tr>
              </tbody>
            </table>
            
            <div className="bg-green-50 px-6 py-4 text-center">
              <p className="text-2xl font-bold text-green-900">
                Save $3,000-5,400 per year
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="border-b pb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Is this legal? Will it violate prison rules?
              </h3>
              <p className="text-gray-600">
                Yes, it's completely legal. The parent records messages during approved visitation 
                or via facility-approved methods. We don't facilitate unauthorized communication. 
                Check your facility's specific rules about recording during visits.
              </p>
            </div>
            
            <div className="border-b pb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                How does the parent record if they can't visit?
              </h3>
              <p className="text-gray-600">
                Options include: (1) Recording during video visits, (2) Using prison library equipment 
                if available, (3) Phone recording system (facility-dependent), (4) Mail-in written 
                messages that we convert to audio using voice cloning.
              </p>
            </div>
            
            <div className="border-b pb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                What if my child doesn't have a phone?
              </h3>
              <p className="text-gray-600">
                Messages can be delivered to your phone, and you play them for your child. 
                Or we can set up a dedicated phone number your child can call from any phone 
                to hear messages.
              </p>
            </div>
            
            <div className="border-b pb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Can the incarcerated parent add new messages?
              </h3>
              <p className="text-gray-600">
                Yes, during future visits or via approved recording methods. You can add messages 
                anytime throughout the incarceration period.
              </p>
            </div>
            
            <div className="border-b pb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                What happens when they're released?
              </h3>
              <p className="text-gray-600">
                You can cancel anytime. Many families continue using it because it helps with 
                reentry - kids are already used to hearing parent's voice, making the transition easier. 
                Studies show this reduces recidivism.
              </p>
            </div>
            
            <div className="border-b pb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Do you work with nonprofits or social services?
              </h3>
              <p className="text-gray-600">
                Yes! We partner with prison reform organizations, reentry programs, and social services. 
                If you're a nonprofit, contact us about bulk pricing and sponsorship programs.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="bg-purple-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-4">
            Affordable. Unlimited. Life-Changing.
          </h2>
          <p className="text-xl text-purple-200 text-center mb-12">
            Stop paying $500/month for prison calls
          </p>
          
          <div className="bg-white rounded-xl p-8 shadow-2xl">
            <div className="text-center mb-6">
              <div className="text-5xl font-bold text-gray-900 mb-2">
                $49<span className="text-2xl text-gray-600">/month</span>
              </div>
              <p className="text-green-600 font-bold text-xl">Save $400+/month vs. prison calls</p>
            </div>
            
            <ul className="space-y-3 mb-8">
              <li className="flex items-center text-gray-700">
                <span className="text-green-500 mr-3 text-xl">✓</span>
                <span><strong>Unlimited messages</strong> - no per-minute fees</span>
              </li>
              <li className="flex items-center text-gray-700">
                <span className="text-green-500 mr-3 text-xl">✓</span>
                <span><strong>24/7 access</strong> - listen anytime, anywhere</span>
              </li>
              <li className="flex items-center text-gray-700">
                <span className="text-green-500 mr-3 text-xl">✓</span>
                <span><strong>No time limits</strong> - replay as many times as needed</span>
              </li>
              <li className="flex items-center text-gray-700">
                <span className="text-green-500 mr-3 text-xl">✓</span>
                <span><strong>Voice cloning</strong> - authentic parent's voice</span>
              </li>
              <li className="flex items-center text-gray-700">
                <span className="text-green-500 mr-3 text-xl">✓</span>
                <span><strong>Automated delivery</strong> - daily bedtime stories</span>
              </li>
              <li className="flex items-center text-gray-700">
                <span className="text-green-500 mr-3 text-xl">✓</span>
                <span><strong>Cancel anytime</strong> - no long-term commitment</span>
              </li>
            </ul>
            
            <button
              onClick={handleGetStarted}
              className="w-full bg-purple-600 text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-purple-700 transition"
            >
              Start Free Trial →
            </button>
            
            <p className="text-center text-gray-600 text-sm mt-4">
              First month free • No credit card required • Cancel anytime
            </p>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-purple-200 mb-2">
              <strong>Nonprofit or social services?</strong>
            </p>
            <a href="/nonprofits" className="text-white underline hover:text-purple-200">
              Contact us about bulk pricing and sponsorship programs →
            </a>
          </div>
        </div>
      </div>

      {/* Impact Section */}
      <div className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            The Impact Goes Beyond Connection
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="text-5xl mb-4">📉</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">20% Lower Recidivism</h3>
              <p className="text-gray-600">
                Parents who stay connected to their kids are 20% less likely to re-offend
              </p>
            </div>
            
            <div className="text-center">
              <div className="text-5xl mb-4">😊</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Better Child Outcomes</h3>
              <p className="text-gray-600">
                Kids with connected parents show reduced anxiety, better school performance
              </p>
            </div>
            
            <div className="text-center">
              <div className="text-5xl mb-4">💰</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Financial Relief</h3>
              <p className="text-gray-600">
                Families save thousands per year, reducing financial stress during incarceration
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Your Child Deserves to Hear Their Parent's Voice
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Stop paying $500/month for 15-minute calls. Get unlimited access for $49/month.
          </p>
          <button
            onClick={handleGetStarted}
            className="bg-green-400 text-purple-900 px-12 py-5 rounded-lg text-2xl font-bold hover:bg-green-300 transition shadow-2xl"
          >
            Start Free Trial →
          </button>
          <p className="text-purple-200 mt-4">
            First month free • Save $400+/month • Help your family stay connected
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrisonFamilies;
